/**
 * Vocabulário do Web Defense — puro, sem React, para o check de contrato.
 * Contrato: Athena/HERMES_WEB_DEFENSE.md (lado do agente: Argos docs/web-defense.md §7).
 *
 * O que a tela não pode errar:
 *  - um challenge só é dado como RESOLVIDO quando o agente responde;
 *    `delivered` é "escrito na stream", nada mais;
 *  - o `challenge_id` só existe no agente que o abriu, então o comando vai para
 *    a máquina do item e nunca para outra;
 *  - `activity` sem resumo vem com contadores 0: um zero que não pode ser lido
 *    como "nenhuma requisição".
 */
import type { Tone } from '../../components/primitives/Metric.tsx'
import type { CommandCatalog } from '../api/admin.ts'
import type { WebChallenge, WebChallengeActivity, WebIpState } from '../api/types.ts'
import { unavailableReasonText } from '../commands/semantics.ts'
import { formatDayClock, formatInt } from '../format.ts'

/* ── comandos ────────────────────────────────────────────────────────────── */

export const WEB_COMMAND_KINDS = [
  'resolve_challenge',
  'challenge_ip',
  'list_web_exceptions',
  'revoke_web_exception',
] as const

export type WebCommandKind = (typeof WEB_COMMAND_KINDS)[number]

/**
 * Disponibilidade de um comando segundo o catálogo, que já considera o papel de
 * quem pergunta (staff recebe `agent_command_requires_ip_permission` nos três
 * comandos que decidem). Sem catálogo (falhou ao carregar), o servidor decide no
 * envio. Um kind que o catálogo não lista é um Athena que ainda não o conhece.
 */
export function availabilityOf(
  catalog: CommandCatalog | null,
  kind: string,
): { available: boolean; reason: string | null } {
  if (catalog === null) return { available: true, reason: null }
  const def = catalog.commands.find((c) => c.kind === kind)
  if (def === undefined) {
    return { available: false, reason: 'Este Athena ainda não oferece os comandos do Web Defense.' }
  }
  return def.available
    ? { available: true, reason: null }
    : { available: false, reason: unavailableReasonText(def.unavailable_reason) }
}

/* ── as três respostas a um challenge ───────────────────────────────────── */

/** Liberar o IP · falso positivo da aplicação · bloquear. */
export type ChallengeChoice = 'allow_ip' | 'allow_rule_route' | 'deny'

export const CHALLENGE_CHOICES: readonly ChallengeChoice[] = ['allow_ip', 'allow_rule_route', 'deny']

interface ChoiceSpec {
  label: string
  decision: 'allow' | 'deny'
  /** `null` no DENY: o Athena recusa escopo junto com `deny`. */
  scope: 'ip' | 'rule_route' | null
  hint: string
}

const CHOICE: Record<ChallengeChoice, ChoiceSpec> = {
  allow_ip: {
    label: 'Liberar o IP',
    decision: 'allow',
    scope: 'ip',
    hint: 'O IP é liberado e aprendido.',
  },
  allow_rule_route: {
    label: 'Falso positivo da aplicação',
    decision: 'allow',
    scope: 'rule_route',
    hint: 'Exceção: a regra deixa de disparar nesta rota, para qualquer IP.',
  },
  deny: {
    label: 'Bloquear',
    decision: 'deny',
    scope: null,
    hint: 'Bloqueio definitivo, no nginx e no firewall.',
  },
}

export function choiceLabel(choice: ChallengeChoice): string {
  return CHOICE[choice].label
}

export function choiceHint(choice: ChallengeChoice): string {
  return CHOICE[choice].hint
}

/** Como a regra aparece nas frases: o nome, senão o id. */
export function ruleText(rule: { rule_id: string | null; rule_name: string | null }): string {
  return rule.rule_name ?? rule.rule_id ?? 'a regra'
}

/** A rota de uma exceção é o caminho, sem a query. */
export function routeText(item: { path: string | null }): string {
  return item.path ?? '/'
}

/** `GET /produtos` — a linha da requisição, sem a query. `null` quando nada veio. */
export function requestLine(item: { method: string | null; path: string | null }): string | null {
  if (item.method === null && item.path === null) return null
  return `${item.method ?? '?'} ${item.path ?? '/'}`
}

/**
 * A frase da confirmação. `null` para "Liberar o IP", que não pede confirmação:
 * é a decisão reversível (um novo bloqueio de operador desfaz o aprendizado).
 */
export function choiceConsequence(
  choice: ChallengeChoice,
  context: { rule: string; route: string },
): string | null {
  switch (choice) {
    case 'allow_ip':
      return null
    case 'allow_rule_route':
      return `A regra ${context.rule} deixa de valer em ${context.route} para todos os IPs.`
    case 'deny':
      return 'Bloqueio definitivo no nginx e no firewall.'
  }
}

/** `params` do `resolve_challenge`, em string JSON. O DENY não leva escopo. */
export function resolveChallengeParams(challengeId: string, choice: ChallengeChoice): string {
  const spec = CHOICE[choice]
  return JSON.stringify(
    spec.scope === null
      ? { challenge_id: challengeId, decision: spec.decision }
      : { challenge_id: challengeId, decision: spec.decision, scope: spec.scope },
  )
}

export function revokeWebExceptionParams(exceptionId: string): string {
  return JSON.stringify({ exception_id: exceptionId })
}

/* ── desfecho de um comando do Web Defense ──────────────────────────────── */

export interface WebOutcome {
  tone: Tone
  headline: string
  /** O agente CONFIRMOU (`executed`/`skipped`). */
  confirmed: boolean
  /** O agente respondeu e o estado no Athena mudou: hora de reler. */
  settled: boolean
}

/** Os estados em que nada aconteceu no agente — iguais para todo comando. */
function unconfirmed(state: string, unchanged: string): WebOutcome {
  const base = { confirmed: false, settled: false }
  switch (state) {
    case 'pending':
      return { ...base, tone: 'accent', headline: `Na fila — aguardando o agente buscar o comando. ${unchanged}` }
    case 'delivered':
      return { ...base, tone: 'warn', headline: `Entregue ao agente, sem confirmação. ${unchanged}` }
    case 'failed':
      return { ...base, tone: 'crit', headline: `O comando FALHOU. ${unchanged} Veja o motivo do agente.` }
    case 'expired':
      return { ...base, tone: 'warn', headline: `O prazo venceu sem resposta do agente. ${unchanged}` }
    case 'cancelled':
      return { ...base, tone: 'neutral', headline: `Comando cancelado. ${unchanged}` }
    default:
      return { ...base, tone: 'neutral', headline: `Estado "${state}" desconhecido.` }
  }
}

/**
 * O que a tela pode AFIRMAR depois de um `resolve_challenge`. `skipped` é
 * confirmação também (já resolvido ou expirado; o `detail` diz quem decidiu
 * antes). `failed` não resolve, mas FECHA o item no Athena: a fila muda.
 */
export function challengeOutcome(
  choice: ChallengeChoice,
  state: string,
  context: { ip: string; rule: string; route: string },
): WebOutcome {
  if (state === 'executed') {
    const headline =
      choice === 'allow_ip'
        ? `IP ${context.ip} liberado e aprendido: o agente confirmou.`
        : choice === 'allow_rule_route'
          ? `Exceção criada: a regra ${context.rule} deixou de valer em ${context.route} para todos os IPs. O agente confirmou.`
          : `IP ${context.ip} bloqueado em definitivo no nginx e no firewall: o agente confirmou.`
    return { tone: 'ok', headline, confirmed: true, settled: true }
  }
  if (state === 'skipped') {
    return {
      tone: 'ok',
      headline: 'Nada a resolver: o challenge já tinha sido resolvido ou expirou.',
      confirmed: true,
      settled: true,
    }
  }
  if (state === 'failed') {
    return {
      tone: 'crit',
      headline: 'O agente NÃO resolveu o challenge.',
      confirmed: false,
      settled: true,
    }
  }
  return unconfirmed(state, 'O challenge segue aberto.')
}

/** Desfecho do `revoke_web_exception`. */
export function revokeExceptionOutcome(state: string, context: { rule: string; route: string }): WebOutcome {
  if (state === 'executed') {
    return {
      tone: 'ok',
      headline: `Exceção revogada: a regra ${context.rule} volta a valer em ${context.route}. O agente confirmou.`,
      confirmed: true,
      settled: true,
    }
  }
  if (state === 'skipped') {
    return { tone: 'ok', headline: 'A exceção já não existia neste agente.', confirmed: true, settled: true }
  }
  return unconfirmed(state, 'A exceção segue valendo.')
}

/* ── o item da fila ─────────────────────────────────────────────────────── */

/**
 * As repetições do IP. Sem resumo, `requests` é `—` e não `0`: o Athena manda 0
 * nesse caso, e 0 diria "o IP parou", que ninguém sabe.
 */
export function activityPresentation(activity: WebChallengeActivity): {
  requests: string
  breakdown: string | null
  hasSummary: boolean
} {
  if (activity.last_seen_at === null) return { requests: '—', breakdown: null, hasSummary: false }
  const parts = [
    activity.blocked > 0 ? `${formatInt(activity.blocked)} com 403` : null,
    activity.limited > 0 ? `${formatInt(activity.limited)} com 429` : null,
    activity.challenged > 0 ? `${formatInt(activity.challenged)} sob challenge` : null,
  ].filter((part) => part !== null)
  return {
    requests: formatInt(activity.requests),
    breakdown: parts.length === 0 ? null : parts.join(' · '),
    hasSummary: true,
  }
}

/**
 * Um item que saiu da fila entre a listagem e a abertura da ficha: o que
 * aconteceu com ele. `null` enquanto pendente.
 */
export function closedPresentation(
  item: Pick<WebChallenge, 'state' | 'resolution' | 'escalation' | 'closed_at'>,
): { tone: Tone; headline: string; detail: string | null } | null {
  switch (item.state) {
    case 'pending':
      return null
    case 'resolved': {
      const r = item.resolution
      if (r === null) return { tone: 'neutral', headline: 'Resolvido.', detail: null }
      const decision =
        r.decision === 'deny' ? 'bloquear' : r.scope === 'rule_route' ? 'falso positivo da aplicação' : 'liberar o IP'
      return r.outcome === 'failed'
        ? { tone: 'crit', headline: `Decisão "${decision}" FALHOU no agente em ${formatDayClock(r.finished_at)} UTC.`, detail: r.detail }
        : { tone: 'ok', headline: `Já resolvido: "${decision}" em ${formatDayClock(r.finished_at)} UTC (${r.outcome}).`, detail: r.detail }
    }
    case 'escalated':
      return {
        tone: 'crit',
        headline: `O Argos bloqueou o IP sozinho em ${formatDayClock(item.escalation?.blocked_at ?? item.closed_at)} UTC.`,
        detail: item.escalation?.detail ?? null,
      }
    case 'expired':
      return { tone: 'neutral', headline: 'Expirou sem decisão: o IP voltou a ser só observado.', detail: null }
    default:
      return { tone: 'neutral', headline: `Fora da fila (estado "${item.state}").`, detail: null }
  }
}

/* ── estado do IP e prazo ───────────────────────────────────────────────── */

export interface IpStatePresentation {
  label: string
  tone: Tone
  hint: string
}

const IP_STATE: Record<string, IpStatePresentation> = {
  observado: { label: 'observado', tone: 'neutral', hint: 'Atendido normalmente: o Argos só observa.' },
  limitado: { label: 'limitado', tone: 'warn', hint: 'Passou do limite de taxa: recebe 429 por 60 s.' },
  challenge: {
    label: 'em challenge',
    tone: 'accent',
    hint: 'Segue atendido, sob monitoramento reforçado.',
  },
  bloqueado: { label: 'bloqueado', tone: 'crit', hint: 'Bloqueado na camada web (e no firewall, quando chega direto).' },
}

export function ipStatePresentation(state: WebIpState | null): IpStatePresentation {
  if (state === null) {
    return { label: 'sem resumo', tone: 'neutral', hint: 'Nenhum resumo do IP chegou desde a abertura.' }
  }
  return IP_STATE[state] ?? { label: state, tone: 'neutral', hint: `Estado "${state}" fora do vocabulário conhecido.` }
}

/** Quanto falta para o challenge expirar: "em 3 h 12 min", "em 8 min", "expirado". */
export function formatRemaining(ms: number): string {
  if (!Number.isFinite(ms)) return '—'
  if (ms <= 0) return 'expirado'
  const minutes = Math.ceil(ms / 60_000)
  if (minutes < 60) return `em ${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `em ${hours} h` : `em ${hours} h ${rest} min`
}
